import React from 'react';
import resumeData from './person.json';
import './App.css';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function formatDate(str) {
  if (!str || str.toLowerCase() === 'present') return 'Present';
  const parts = str.split('-');
  if (parts.length === 1) return parts[0];
  const y = parts[0];
  const m = parseInt(parts[1], 10);
  return m ? `${MONTHS[m - 1]} ${y}` : y;
}

function shortenKeyword(kw) {
  const colonIdx = kw.indexOf(':');
  if (colonIdx > 0 && colonIdx < 30) return kw.substring(0, colonIdx);
  if (kw.length > 35) return kw.substring(0, 32) + '…';
  return kw;
}

function resolvePicture(url) {
  if (!url) return null;
  if (url.startsWith('./')) return process.env.PUBLIC_URL + '/' + url.slice(2);
  return url;
}

/* Ongoing roles first, then the most recently finished ones. Sorting on
   startDate alone buries a current role under a shorter engagement that
   started later. ISO dates sort lexicographically, so string compare is enough. */
function isOngoing(job) {
  return !job.endDate || job.endDate.toLowerCase() === 'present';
}

function byRecencyDesc(a, b) {
  if (isOngoing(a) !== isOngoing(b)) return isOngoing(a) ? -1 : 1;
  return (b.endDate || '').localeCompare(a.endDate || '')
      || (b.startDate || '').localeCompare(a.startDate || '');
}

function telHref(phone) {
  // "+40 752 827 666" -> "tel:+40752827666"
  return 'tel:' + phone.replace(/[^+\d]/g, '');
}

/* ── SVG Icons (decorative) ──────────────────── */
const IconEmail = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);
const IconPhone = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 01-2.18 2A19.79 19.79 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 .01h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
  </svg>
);
const IconGlobe = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
  </svg>
);
const IconPin = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);
const IconGithub = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/>
  </svg>
);

function profileIcon(network) {
  if ((network || '').toLowerCase().includes('github')) return <IconGithub />;
  return <IconGlobe />;
}

/* ── Export PDF button ───────────────────────── */
const IconDownload = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

function ExportButton() {
  return (
    <button
      className="rv-export-btn"
      onClick={() => window.print()}
      title="Opens the print dialog — choose 'Save as PDF'"
      aria-label="Print or save as PDF"
    >
      <span className="rv-export-icon"><IconDownload /></span>
      <span className="rv-export-label">Print / Save PDF</span>
    </button>
  );
}

/* ── Component ───────────────────────────────── */
export default function App() {
  const { basics, work, education, skills, languages } = resumeData;
  const photoSrc = resolvePicture(basics.picture);
  const jobs = work.filter(job => job.visible !== false).sort(byRecencyDesc);

  return (
    <div className="rv-page">
      <ExportButton />
      <div className="rv-card">

        {/* ── SIDEBAR: identity + contact ── */}
        <aside className="rv-sidebar rv-sidebar--top">
          <div className="rv-profile">
            {photoSrc && (
              <div className="rv-avatar-ring">
                <img src={photoSrc} alt={basics.name} className="rv-avatar" />
              </div>
            )}
            <p className="rv-profile-name">{basics.name}</p>
            <span className="rv-badge">{basics.label}</span>
          </div>

          <div className="rv-divider" />

          <section className="rv-section">
            <h2 className="rv-section-label">Contact</h2>
            <ul className="rv-contact">
              <li>
                <span className="rv-icon"><IconEmail /></span>
                <a href={`mailto:${basics.email}`}>{basics.email}</a>
              </li>
              <li>
                <span className="rv-icon"><IconPhone /></span>
                <a href={telHref(basics.phone)}>{basics.phone}</a>
              </li>
              <li>
                <span className="rv-icon"><IconGlobe /></span>
                <a href={basics.website} target="_blank" rel="noopener noreferrer">
                  {basics.website.replace(/^https?:\/\/(www\.)?/, '')}
                </a>
              </li>
              <li>
                <span className="rv-icon"><IconPin /></span>
                <span>{basics.location.city}, {basics.location.region}</span>
              </li>
              {basics.profiles.map(p => (
                <li key={p.network}>
                  <span className="rv-icon">{profileIcon(p.network)}</span>
                  <a href={p.url} target="_blank" rel="noopener noreferrer">
                    {p.url.replace(/^https?:\/\/(www\.)?/, '')}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        </aside>

        {/* ── MAIN ── */}
        <main className="rv-main">
          <header className="rv-header">
            <div>
              <h1 className="rv-h1">{basics.name}</h1>
              <p className="rv-role">{basics.label}</p>
            </div>
            {basics.summary && (
              <blockquote className="rv-summary">{basics.summary}</blockquote>
            )}
          </header>

          <section className="rv-main-section">
            <h2 className="rv-main-heading">
              <svg aria-hidden="true" className="rv-heading-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd"/>
                <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z"/>
              </svg>
              Work Experience
            </h2>
            <div className="rv-timeline">
              {jobs.map(job => (
                <div key={`${job.company}-${job.startDate}`} className="rv-tl-item">
                  <div className="rv-tl-dot" />
                  <div className="rv-tl-body">
                    <div className="rv-tl-top">
                      <div className="rv-tl-titles">
                        <span className="rv-tl-position">{job.position}</span>
                        <a
                          href={job.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rv-tl-company"
                        >
                          {job.company}
                        </a>
                      </div>
                      <span className="rv-tl-date">
                        {formatDate(job.startDate)} – {formatDate(job.endDate)}
                      </span>
                    </div>
                    {job.highlights && job.highlights.length > 0 && (
                      <ul className="rv-tl-bullets">
                        {job.highlights.map((h, j) => <li key={j}>{h}</li>)}
                      </ul>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="rv-main-section">
            <h2 className="rv-main-heading">
              <svg aria-hidden="true" className="rv-heading-icon" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z"/>
              </svg>
              Education
            </h2>
            <div className="rv-timeline">
              {education.map(edu => (
                <div key={`${edu.institution}-${edu.startDate}`} className="rv-tl-item">
                  <div className="rv-tl-dot" />
                  <div className="rv-tl-body">
                    <div className="rv-tl-top">
                      <div className="rv-tl-titles">
                        <span className="rv-tl-position">{edu.studyType}</span>
                        <span className="rv-tl-company rv-tl-company--plain">{edu.institution}</span>
                      </div>
                      <span className="rv-tl-date">
                        {formatDate(edu.startDate)} – {formatDate(edu.endDate)}
                      </span>
                    </div>
                    {edu.area && <p className="rv-tl-area">{edu.area}</p>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </main>

        {/* ── SIDEBAR: skills + languages (left rail on desktop; after the
               main content on mobile and in the printed PDF) ── */}
        <aside className="rv-sidebar rv-sidebar--bottom">
          <section className="rv-section">
            <h2 className="rv-section-label">Languages</h2>
            <div className="rv-languages">
              {languages.map(l => (
                <div key={l.name} className="rv-lang-row">
                  <span className="rv-lang-name">{l.name}</span>
                  <span className="rv-lang-level">{l.level}</span>
                </div>
              ))}
            </div>
          </section>

          <div className="rv-divider" />

          <section className="rv-section">
            <h2 className="rv-section-label">Skills</h2>
            <div className="rv-skills">
              {skills.map(s => (
                <div key={s.name} className="rv-skill">
                  <div className="rv-skill-row">
                    <span className="rv-skill-name">{s.name}</span>
                  </div>
                  <div className="rv-tags">
                    {s.keywords.map((kw, i) => (
                      <span key={i} className="rv-tag" title={kw}>{shortenKeyword(kw)}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </aside>

      </div>
    </div>
  );
}
